const W = 1080, H = 1920;
const INK = "#111827", GOLD = "#d4a017", PAPER = "#fdf6e3", MUTED = "#9ca3af";
const FONT = "Inter, 'Helvetica Neue', Arial, sans-serif";

export const CARD_THEMES = {
  ink: { id: "ink", label: "Ink", bg: INK, grain: "253,246,227", frame: GOLD, text: "#ffffff", sub: PAPER, muted: MUTED, card: PAPER, cardText: INK, cardSub: "#4b5563", wordmark: "#ffffff" },
  paper: { id: "paper", label: "Cream paper", bg: PAPER, grain: "17,24,39", frame: INK, text: INK, sub: "#374151", muted: "#6b7280", card: INK, cardText: "#ffffff", cardSub: "#d1d5db", wordmark: INK },
};

const loadImage = (src) => new Promise((resolve) => {
  const img = new Image();
  img.onload = () => resolve(img);
  img.onerror = () => resolve(null);
  img.src = src;
});

const roundRect = (ctx, x, y, w, h, r) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
};

const fitText = (ctx, text, maxWidth, size, weight) => {
  let s = size;
  do { ctx.font = `${weight} ${s}px ${FONT}`; s -= 4; } while (ctx.measureText(text).width > maxWidth && s > 24);
};

export async function drawTwinShareCard(canvas, { name, dateLabel, signName, glyph, twins, theme = "ink", labels = {} }) {
  const L = { kicker: "MY BIRTHDAY TWINS", sub: "I SHARE MY BIRTHDAY WITH", row: (i) => `Birthday twin #${i + 1}`, empty: "No famous twins on record yet — I might be the first.", cta: "Discover yours on oddlytrue.co", ...labels };
  const T = CARD_THEMES[theme] || CARD_THEMES.ink;
  if (document.fonts?.ready) await document.fonts.ready;
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d");

  ctx.fillStyle = T.bg; ctx.fillRect(0, 0, W, H);
  for (let i = 0; i < 1800; i++) { // paper-grain speckle
    ctx.fillStyle = `rgba(${T.grain},${Math.random() * 0.05})`;
    ctx.fillRect(Math.random() * W, Math.random() * H, 2, 2);
  }
  ctx.strokeStyle = T.frame; ctx.lineWidth = 6; ctx.strokeRect(48, 48, W - 96, H - 96);
  ctx.lineWidth = 2; ctx.strokeRect(72, 72, W - 144, H - 144);

  const mark = await loadImage("/seal-oddlytrue-red-wax.png");
  if (mark) ctx.drawImage(mark, 112, 128, 120, 120);
  ctx.textBaseline = "middle"; ctx.textAlign = "left";
  ctx.font = `800 64px ${FONT}`; ctx.fillStyle = T.wordmark; ctx.fillText("Oddly", 240, 188);
  ctx.fillStyle = GOLD; ctx.fillText("True", 240 + ctx.measureText("Oddly").width, 188);

  ctx.textAlign = "center";
  ctx.fillStyle = GOLD; ctx.font = `700 34px ${FONT}`;
  ctx.letterSpacing = "10px"; ctx.fillText(L.kicker, W / 2, 400); ctx.letterSpacing = "0px";
  ctx.fillStyle = T.text; fitText(ctx, dateLabel, W - 240, 132, 800); ctx.fillText(dateLabel, W / 2, 520);
  ctx.fillStyle = T.sub; ctx.font = `500 40px ${FONT}`;
  ctx.fillText(`${glyph ? glyph + "  " : ""}${signName || ""}${name ? `${signName ? "  ·  " : ""}${name}` : ""}`, W / 2, 610);

  ctx.strokeStyle = GOLD; ctx.lineWidth = 3; ctx.beginPath(); ctx.moveTo(240, 690); ctx.lineTo(W - 240, 690); ctx.stroke();
  ctx.fillStyle = T.muted; ctx.font = `600 30px ${FONT}`; ctx.letterSpacing = "6px";
  ctx.fillText(L.sub, W / 2, 760); ctx.letterSpacing = "0px";

  const rows = twins.slice(0, 3);
  rows.forEach((t, i) => {
    const y = 830 + i * 250;
    roundRect(ctx, 120, y, W - 240, 200, 28); ctx.fillStyle = T.card; ctx.fill();
    ctx.save(); ctx.clip(); ctx.fillStyle = GOLD; ctx.fillRect(120, y, 14, 200); ctx.restore();
    ctx.textAlign = "left"; ctx.fillStyle = T.cardText; fitText(ctx, t.name, W - 240 - 320, 60, 800); ctx.fillText(t.name, 180, y + 78);
    ctx.font = `500 34px ${FONT}`; ctx.fillStyle = T.cardSub; ctx.fillText(L.row(i), 180, y + 140);
    ctx.textAlign = "right"; ctx.fillStyle = GOLD; ctx.font = `800 72px ${FONT}`; ctx.fillText(String(t.year ?? ""), W - 170, y + 100);
  });
  if (rows.length === 0) {
    ctx.textAlign = "center"; ctx.fillStyle = T.sub; ctx.font = `500 40px ${FONT}`;
    ctx.fillText(L.empty, W / 2, 930);
  }

  ctx.textAlign = "center"; ctx.fillStyle = GOLD; ctx.font = `700 40px ${FONT}`;
  ctx.fillText(L.cta, W / 2, 1700);
  ctx.fillStyle = T.muted; ctx.font = `500 28px ${FONT}`;
  ctx.fillText("Star signs · birthday twins · on this day in history", W / 2, 1760);
  return canvas;
}

export async function renderTwinShareCard(data) {
  const canvas = await drawTwinShareCard(document.createElement("canvas"), data);
  return new Promise((resolve) => canvas.toBlob((blob) => resolve(blob), "image/png"));
}

export async function downloadTwinShareCard(data) {
  const blob = await renderTwinShareCard(data);
  const url = URL.createObjectURL(blob);
  const a = Object.assign(document.createElement("a"), { href: url, download: `oddlytrue-birthday-twins-${(data.dateLabel || "card").replace(/\s+/g, "-").toLowerCase()}-${data.theme || "ink"}.png` });
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}
